// src/pages/ScannerPage/ScannerPage.js
import React, { useState, useEffect } from 'react';
import QRReaderWrapper from '../../components/scanner/QRReaderWrapper';
import './ScannerPage.css';
import { publicApi } from '../../services/api';
import config from '../../config/config';

/**
 * Page de scan des QR codes pour l'entrée à l'événement
 */
const ScannerPage = () => {
  const [scanning, setScanning] = useState(true);
  const [result, setResult] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [scanStatus, setScanStatus] = useState('ready'); // 'ready', 'success', 'error', 'already-scanned'
  const [errorMessage, setErrorMessage] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [qrReaderError, setQrReaderError] = useState(null);
  const [offlineScansCount, setOfflineScansCount] = useState(0);

  // Vérifier le statut de la connexion
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Récupérer les scans récents depuis le localStorage
    const storedScans = localStorage.getItem('recentScans');
    if (storedScans) {
      try {
        setRecentScans(JSON.parse(storedScans).slice(0, 10)); // Limiter à 10 scans
      } catch (e) {
        console.error('Error parsing stored scans:', e);
      }
    }
    
    // Récupérer le compteur de scans hors ligne
    const offlineScans = localStorage.getItem('offlineScans');
    if (offlineScans) {
      try {
        const scans = JSON.parse(offlineScans);
        setOfflineScansCount(scans.filter(scan => !scan.synced).length);
      } catch (e) {
        console.error('Error parsing offline scans:', e);
      }
    }
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Gérer le scan d'un QR code
  const handleScan = async (data) => {
    if (data && scanning) {
      setScanning(false); // Arrêter temporairement le scan
      setResult(data);
      
      try {
        // Traiter le scan du QR code
        if (isOnline) {
          // Mode en ligne : envoyer au serveur
          setScanStatus('processing');
          try {
            const response = await publicApi.scanQRCode(data);
            
            if (response && response.success) {
              setScanStatus('success');
              
              // Ajouter au début des scans récents
              const newScan = {
                id: response.entry.id || Date.now(),
                name: response.entry.name,
                phone: response.entry.phone,
                ticketCode: response.entry.ticketCode,
                scanTime: response.entry.scanTime || new Date().toISOString(),
                isOffline: false
              };
              
              updateRecentScans(newScan);
            }
          } catch (error) {
            console.error('Scan error:', error);
            //setScanStatus('error');
            
            if (error.response && error.response.data && error.response.data.message && error.response.data.message.includes("pas encore été validé")) {
				setScanStatus('not-validated');
				setErrorMessage("Ce ticket n'a pas encore été validé par l'administrateur");
              
              // Vérifier si c'est un scan en double
			} else if (error.response && error.response.status === 409 || (error.response.data.message && error.response.data.message.includes('déjà'))) {
				setScanStatus('already-scanned');
				setErrorMessage("Ce ticket a déjà été scanné");
            } else {
				setScanStatus('error');
				setErrorMessage(error.response?.data?.message || 'Erreur lors du scan');
            }
          }
        } else {
          // Mode hors ligne : stocker localement
          setScanStatus('success');
          const offlineScan = {
            id: Date.now(),
            qrData: data,
            scanTime: new Date().toISOString(),
            isOffline: true,
            synced: false,
            scannerId: scannerId // Ajouter l'ID du scanner
          };
          
          // Extraire les infos du QR code (format: GUINXXXX-ID-PHONE)
          let name = 'Utilisateur';
          let phone = '';
          let ticketCode = '';
          
          try {
            const parts = data.split('-');
            if (parts.length >= 3) {
              ticketCode = parts[0];
              phone = parts[2];
            }
          } catch (e) {
            console.error('Error parsing QR data:', e);
          }
          
          // Ajouter aux scans récents
          const newScan = {
            id: offlineScan.id,
            name: name,
            phone: phone,
            ticketCode: ticketCode || 'Ticket inconnu',
            scanTime: offlineScan.scanTime,
            isOffline: true
          };
          
          updateRecentScans(newScan);
          
          // Stocker dans le localStorage pour synchronisation ultérieure
          const storedOfflineScans = localStorage.getItem('offlineScans');
          const offlineScans = storedOfflineScans ? JSON.parse(storedOfflineScans) : [];
          offlineScans.push(offlineScan);
          localStorage.setItem('offlineScans', JSON.stringify(offlineScans));
          
          // Mettre à jour le compteur
          setOfflineScansCount(offlineScans.filter(scan => !scan.synced).length);
        }
      } catch (e) {
        console.error('Error processing scan:', e);
        setScanStatus('error');
        setErrorMessage('Erreur lors du traitement du scan');
      }
      
      // Réactiver le scan après 3 secondes
      setTimeout(() => {
        setScanning(true);
        setScanStatus('ready');
        setResult(null);
        setErrorMessage('');
      }, 3000);
    }
  };

  // Mettre à jour la liste des scans récents
  const updateRecentScans = (newScan) => {
    setRecentScans(prevScans => {
      const updatedScans = [newScan, ...prevScans].slice(0, 10); // Limiter à 10 scans
      localStorage.setItem('recentScans', JSON.stringify(updatedScans));
      return updatedScans;
    });
  };

  // Gérer les erreurs de scan
  const handleError = (err) => {
    console.error('QR Scanner error:', err);
    setQrReaderError(err);
  };

  // Formater le numéro de téléphone pour l'affichage
  const formatPhone = (phone) => {
    if (!phone) return '';
    
    // Format: 6XX XX XX XX
    return phone.replace(/(\d{3})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4');
  };

  // Réinitialiser les scans
  const handleReset = () => {
    setScanning(true);
    setScanStatus('ready');
    setResult(null);
    setErrorMessage('');
    setQrReaderError(null);
  };

  // Synchroniser les scans hors ligne
  const handleSync = async () => {
    if (!isOnline) {
      alert('Vous êtes hors ligne. Veuillez vous connecter à Internet pour synchroniser les scans.');
      return;
    }
    
    setScanStatus('processing');
    
    try {
      // Récupérer les scans hors ligne depuis localStorage
      const storedOfflineScans = localStorage.getItem('offlineScans');
      if (!storedOfflineScans) {
        alert('Aucun scan hors ligne à synchroniser.');
        return;
      }
      
      const offlineScans = JSON.parse(storedOfflineScans);
      const unsynced = offlineScans.filter(scan => !scan.synced);
      
      if (unsynced.length === 0) {
        alert('Aucun scan hors ligne à synchroniser.');
        return;
      }
      
      // Envoyer les scans au serveur
      const response = await publicApi.syncOfflineScans(unsynced);
      
      if (response && response.success) {
        // Marquer les scans comme synchronisés
        const updated = offlineScans.map(scan => {
          if (!scan.synced) {
            return { ...scan, synced: true };
          }
          return scan;
        });
        
        localStorage.setItem('offlineScans', JSON.stringify(updated));
        setOfflineScansCount(0);
        
        // Afficher un message de succès
        alert(`${unsynced.length} scan(s) synchronisé(s) avec succès!`);
        
        // Mettre à jour l'état local pour refléter les changements
        setRecentScans(prevScans => 
          prevScans.map(scan => 
            scan.isOffline ? { ...scan, isOffline: false } : scan
          )
        );
      } else {
        alert('Erreur lors de la synchronisation des scans hors ligne.');
      }
    } catch (error) {
      console.error('Sync error:', error);
      alert('Erreur lors de la synchronisation des scans hors ligne.');
    } finally {
      setScanStatus('ready');
    }
  };

  // Erreurs de QRReader traitées directement par le composant QRReaderWrapper

  return (
    <div className="scanner-container">
      <div className="scanner-header">
        <img src="/assets/images/guinness-logo.png" alt="Guinness" className="scanner-logo" />
        <h1>Scanner d'Entrées</h1>
        <div className="scanner-status">
          <span className={`status-indicator ${isOnline ? 'online' : 'offline'}`}></span>
          <span className="status-text">{isOnline ? 'En ligne' : 'Hors ligne'}</span>
        </div>
      </div>
      
      {/* Section de scan */}
      <div className="scanner-section">
        <div className="qr-scanner">
          <QRReaderWrapper
            onResult={(result) => {
              if (result) {
                handleScan(result.getText());
              }
            }}
            onError={handleError}
            scanning={scanning}
          />
        </div>
        
        {/* Résultat du scan */}
        {!scanning && (
          <div className={`scan-result ${scanStatus}`}>
            {scanStatus === 'success' && (
              <>
                <div className="success-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <h2>Ticket validé</h2>
                {result && <p className="scan-data">{result}</p>}
              </>
            )}
			
			{scanStatus === 'not-validated' && (
			  <>
				<div className="warning-icon">
				  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#FF9800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<circle cx="12" cy="12" r="10"></circle>
					<line x1="12" y1="8" x2="12" y2="12"></line>
					<line x1="12" y1="16" x2="12.01" y2="16"></line>
				  </svg>
				</div>
				<h2>Ticket non validé</h2>
				<p>Ce ticket n'a pas encore été validé par l'administrateur</p>
			  </>
			)}
            
            {scanStatus === 'error' && (
              <>
                <div className="error-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </svg>
                </div>
                <h2>Erreur</h2>
                <p className="error-message">{errorMessage}</p>
              </>
            )}
            
            {scanStatus === 'already-scanned' && (
              <>
                <div className="warning-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
                <h2>Déjà scanné</h2>
                <p>Ce ticket a déjà été utilisé</p>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Section pour les scans hors ligne en attente */}
      {!isOnline && offlineScansCount > 0 && (
        <div className="offline-scans-alert" onClick={handleSync}>
          <span className="offline-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="1" y1="1" x2="23" y2="23"></line>
              <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
              <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
              <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
              <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
              <line x1="12" y1="20" x2="12.01" y2="20"></line>
            </svg>
          </span>
          <span>{offlineScansCount} scan(s) en attente de synchronisation</span>
          <span className="sync-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2v6h-6"></path>
              <path d="M3 12a9 9 0 0 1 15-6.7l3-3"></path>
              <path d="M3 22v-6h6"></path>
              <path d="M21 12a9 9 0 0 1-15 6.7l-3 3"></path>
            </svg>
          </span>
        </div>
      )}
      
      {/* Section pour l'historique des scans récents */}
      <div className="recent-scans-section">
        <h2>Scans récents</h2>
        
        {recentScans.length === 0 ? (
          <div className="no-scans">
            <p>Aucun scan récent</p>
          </div>
        ) : (
          <div className="scans-list">
            {recentScans.map((scan) => (
              <div key={scan.id} className={`scan-item ${scan.isOffline ? 'offline-scan' : ''}`}>
                <div className="scan-details">
                  <div className="scan-user">
                    <span className="scan-name">{scan.name}</span>
                    <span className="scan-phone">{formatPhone(scan.phone)}</span>
                  </div>
                  <div className="scan-info">
                    <span className="scan-ticket">{scan.ticketCode}</span>
                    <span className="scan-time">{new Date(scan.scanTime).toLocaleTimeString()}</span>
                  </div>
                </div>
                {scan.isOffline && (
                  <span className="offline-badge">Hors ligne</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScannerPage;